import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
  AfterViewInit
} from '@angular/core';
import {
  CommonTableComponent,
  TableColumn
} from 'src/app/components/common/common-table/common-table.component';
import { RenewalsJanusActionComponent } from './cells-janus/renewals-janus-action-cell/renewals-janus-action.component';
import { RenewalsJanusViewDetailsComponent } from './renewals-janus-view-details/renewals-janus-view-details.component';
import { RenewalsJanusApplicationComponent } from './renewals-janus-application/renewals-janus-application.component';
import { RenewalsJanusRenewComponent } from './renewals-janus-renew/renewals-janus-renew.component';
import { DataService } from 'src/app/services/data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RenewalsJanusService } from '../renewals-janus.service';

const STATUS_CONFIG = {
  disableAllowed: ['approved', 'denied', 'inactive'],
  enableAllowed: ['disabled'],
  deleteBlocked: ['deleted'],
  renewBlocked: ['pending', 'deleted']
};

@Component({
  selector: 'wm-renewals-janus-table',
  standalone: false,
  templateUrl: './renewals-janus-table.component.html',
  styleUrls: ['./renewals-janus-table.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RenewalsJanusTableComponent
  implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(CommonTableComponent)
  table!: CommonTableComponent;

  @Input() data: any[] = [];
  @Input() serverSidePagination = false;
  @Input() currentPage = 0;
  @Input() totalPages = 0;
  @Input() totalRecords = 0;
  @Input() pageSize = 10;
  @Input() isLoading = false;
  @Input() pageSizeStorageKey: string | null = null;
  @Input() sortField: string | null;
  @Input() sortDescending: boolean;

  @Output() pageChange = new EventEmitter<number>();
  @Output() rowsPerPageChange = new EventEmitter<number>();
  @Output() sortChanged = new EventEmitter<{
    field: string;
    descending: boolean;
  }>();
  @Output() columnsChange = new EventEmitter<TableColumn[]>();
  @Output() displayedColumnsChange = new EventEmitter<string[]>();

  modalData: any = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: false,
    okBtn: false,
    closeBtn: false,
    inTime: 150,
    outTime: 150
  };

  selectedRow: any = null;
  currentAction: 'disable' | 'enable' | 'delete' | null = null;

  expandedRow: any | null = null;
  expandedComponent: any = null;
  expandedComponentInputs: any;
  private pendingInlineRouteView:
    | 'details'
    | 'application'
    | 'renew'
    | null = null;
  private pendingInlineRouteId: string | null = null;

  columns: TableColumn[] = [];

  constructor(
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private renewalsJanusSvc: RenewalsJanusService
  ) {}

  canEnable(row: any): boolean {
    const status = (row.status || '').toLowerCase();
    return STATUS_CONFIG.enableAllowed.includes(status);
  }

  canDisable(row: any): boolean {
    const status = (row.status || '').toLowerCase();
    return STATUS_CONFIG.disableAllowed.includes(status);
  }

  canDelete(row: any): boolean {
    const status = (row.status || '').toLowerCase();
    return !STATUS_CONFIG.deleteBlocked.includes(status);
  }

  canRenew(row: any): boolean {
    const status = (row.status || '').toLowerCase();
    return !STATUS_CONFIG.renewBlocked.includes(status);
  }

  openActionModal(row: any, action: 'disable' | 'enable' | 'delete') {
    this.selectedRow = row;
    this.currentAction = action;

    if (action === 'disable') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Disable Registration',
        modalSubDescp: 'Are you sure you want to disable this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }

    if (action === 'enable') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Enable Registration',
        modalSubDescp:
          'This registration will be restored to a status determined by its expiration date and/or requirements.\n\nAre you sure you want to enable this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }

    if (action === 'delete') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Delete Registration',
        modalSubDescp: 'Are you sure you want to delete this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const id = params.get('id');
      const registrationId = params.get('registrationId');
      const view = params.get('view') as
        | 'details'
        | 'application'
        | 'renew'
        | null;

      if (!id) {
        this.clearInlineState();
      }

      this.pendingInlineRouteId = registrationId || id;
      this.pendingInlineRouteView = view;
      this.tryRestoreInlineRouteView();
    });

    this.columns = [
      // Added by Amisha on 2026-06-15.
      // Reason: Renewals Janus should not render the shared leading selection checkbox column.
      {
        key: 'appDescription',
        label: 'Registration Info',
        width: '220px',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'workflowName',
        label: 'App Type',
        width: '232px',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'registeredOn',
        label: 'Registered On',
        width: '130px',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'effectiveOn',
        label: 'Effective On',
        width: '147px',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'expiresOn',
        label: 'Expires On',
        width: '112px',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'status',
        label: 'Status',
        width: '100px',
        headerType: 'text',
        cellType: 'status'
      } as any,

      {
        key: 'action',
        label: 'Actions',
        width: '80px',
        headerType: 'text',
        cellType: 'component',
        component: RenewalsJanusActionComponent,
        componentInputs: (row: any) => {
          return {
            row,
            viewDetails: (r: any) => {
              this.openInline('details', r);
              this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {
                  p: this.getCurrentRoutePage(),
                  view: 'details',
                  id: r.id,
                  registrationId: r.id
                },
                queryParamsHandling: 'merge'
              });
            },
            viewApplication: (r: any) => {
              this.openInline('application', r);
              this.router
                .navigate([], {
                  relativeTo: this.route,
                  queryParams: {
                    p: this.getCurrentRoutePage(),
                    view: 'application',
                    id: r.id,
                    registrationId: r.id
                  },
                  queryParamsHandling: 'merge'
                })
                .then(async () => {
                  await this.applyStepperHeight();
                });
            },
            renew: (r: any) => {
              this.openInline('renew', r);
              this.router
                .navigate([], {
                  relativeTo: this.route,
                  queryParams: {
                    p: this.getCurrentRoutePage(),
                    view: 'renew',
                    id: r.id,
                    registrationId: r.id
                  },
                  queryParamsHandling: 'merge'
                })
                .then(async () => {
                  await this.applyStepperHeight();
                });
            },
            disable: (r: any) => this.openActionModal(r, 'disable'),
            enable: (r: any) => this.openActionModal(r, 'enable'),
            delete: (r: any) => this.openActionModal(r, 'delete'),
            canEnable: (r: any) => this.canEnable(r),
            canDisable: (r: any) => this.canDisable(r),
            canDelete: (r: any) => this.canDelete(r),
            canRenew: (r: any) => this.canRenew(r)
          };
        }
      } as any
    ];

    this.columnsChange.emit(this.columns);
  }

  private async applyStepperHeight(): Promise<void> {
    try {
      const el1 = await this.waitForElement('.sidebar-child-stepper');

      el1.style.setProperty('max-height', '100%', 'important');

      const interval = setInterval(() => {
        const el2 = el1.querySelector('.wizard-steps') as HTMLElement;

        if (el2) {
          el2.style.setProperty('max-height', '100%', 'important');
          clearInterval(interval);
        }
      }, 200);
    } catch (err) {
      console.error(err);
    }
  }

  private waitForElement(selector: string): Promise<HTMLElement> {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        const el = document.querySelector(selector) as HTMLElement;

        if (el) {
          clearInterval(interval);
          resolve(el);
        }
      }, 200);

      setTimeout(() => {
        clearInterval(interval);
        reject(`Element not found: ${selector}`);
      }, 10000);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.tryRestoreInlineRouteView();
    }
  }

  ngAfterViewInit(): void {
    this.tryRestoreInlineRouteView();
  }

  openInline(type: 'details' | 'application' | 'renew', row: any) {
    if (!this.table) {
      console.error('CommonTable not ready');
      return;
    }

    this.table.expandRow(row);

    switch (type) {
      case 'details':
        this.expandedComponent = RenewalsJanusViewDetailsComponent;
        break;

      case 'application':
        this.expandedComponent = RenewalsJanusApplicationComponent;
        break;

      case 'renew':
        this.expandedComponent = RenewalsJanusRenewComponent;
        break;
    }

    this.expandedComponentInputs = (tableRow: any) => ({
      row: tableRow,
      closeExpansion: () => {
        this.closeInlineRoute();
      }
    });
  }

  closeInline(): void {
    this.clearInlineState();
  }

  private tryRestoreInlineRouteView(): void {
    if (
      !this.table ||
      !this.pendingInlineRouteId ||
      !this.pendingInlineRouteView
    ) {
      return;
    }

    const row = (this.data || []).find(
      r => r?.id === this.pendingInlineRouteId
    );
    if (!row) {
      return;
    }

    this.openInline(this.pendingInlineRouteView, row);
  }

  private getCurrentRoutePage(): number {
    return (this.table?.currentPage ?? this.currentPage ?? 0) + 1;
  }

  handlePageChange(page: number): void {
    this.currentPage = page;
    this.pageChange.emit(page);
  }

  handleRowsPerPageChange(size: number): void {
    this.pageSize = size;
    this.rowsPerPageChange.emit(size);
  }

  private closeInlineRoute(): void {
    this.clearInlineState();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        id: null,
        registrationId: null,
        view: null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private clearInlineState(): void {
    this.expandedRow = null;
    this.expandedComponent = null;
    this.expandedComponentInputs = null;

    if (this.table) {
      this.table.expandedRow = null;
    }
  }

  confirmAction(): void {
    if (!this.selectedRow?.id || !this.currentAction) return;

    const row = this.selectedRow;
    const action = this.currentAction;

    this.modalData = {
      ...this.modalData,
      open: false
    };

    if (action === 'enable') {
      this.dataService.reEnableRegistration(row.id).subscribe({
        next: (res: boolean) => {
          if (res) {
            row.disabled = false;
            this.renewalsJanusSvc.setData({
              ...this.renewalsJanusSvc.getData(),
              reRunJanusApplicationDataTable: true
            });
            this.openSuccessModal(action);
          }
        },
        error: err => console.error(err)
      });
    }

    if (action === 'disable') {
      row._previousStatus = row.status;

      this.dataService.disableRegistration(row.id).subscribe({
        next: (res: boolean) => {
          if (res) {
            row.status = 'Disabled';
            row.disabled = true;
            this.table.dataSource.data = [...this.data];
            this.openSuccessModal(action);
          }
        },
        error: err => console.error(err)
      });
    }

    if (action === 'delete') {
      this.dataService.deleteRegistration(row.id).subscribe({
        next: () => {
          this.data = this.data.filter(r => r.id !== row.id);
          this.table.dataSource.data = [...this.data];
          this.openSuccessModal(action);
        },
        error: err => console.error(err)
      });
    }

    this.currentAction = null;
  }

  getSuccessMessage(action: string): string {
    switch (action) {
      case 'disable':
        return 'Your registration has been disabled successfully.';

      case 'enable':
        return 'Your registration has been enabled successfully.';

      case 'delete':
        return 'Your registration has been deleted successfully.';

      default:
        return 'Action completed successfully.';
    }
  }

  openSuccessModal(action: string): void {
    this.modalData = {
      ...this.modalData,
      open: true,
      modalDescp: 'Renewals',
      modalSubDescp: this.getSuccessMessage(action),
      yesNoBtn: false,
      okBtn: true,
      closeBtn: false
    };
  }

  closeModal(): void {
    this.modalData = {
      ...this.modalData,
      open: false
    };

    this.currentAction = null;
    this.selectedRow = null;
    this.table.dataSource.data = [...this.data];
  }

  cancelAction(): void {
    this.modalData = {
      ...this.modalData,
      open: false
    };
    this.currentAction = null;
  }
}
